// src/components/PrivateRoute.jsx
import { useEffect, useRef } from "react";
import { Navigate, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "./AuthContext";
import { signOut } from "firebase/auth";
import { auth } from "../../firebase";
// Re-use the CRM dashboard's wave-dot loading screen so the visual is
// continuous from sign-in through "auth resolved → businesses fetched → CRM
// data ready". Previously this route rendered a bare "Loading…" text on a
// white page that flashed for a few hundred ms before CRMDashboard's styled
// spinner replaced it. Importing the CSS without the JS keeps this file
// dependency-free of the heavy dashboard module.
import "../../pages/CRMDashboard.css";

const SEVEN_DAYS = 7 * 24 * 60 * 60 * 1000;
const THIRTY_MIN = 30 * 60 * 1000;

export default function PrivateRoute() {
  const { user, userInfo, userInfoLoading, userMissing, loading } = useAuth();
  const navigate = useNavigate();
  const timerRef = useRef(null);

  useEffect(() => {
    if (!user) return;

    const keepLoggedIn = localStorage.getItem("keepLoggedIn") === "true";
    const loginTime = parseInt(localStorage.getItem("loginTime") || "0", 10);

    // 7-day absolute expiry for "keep me logged in"
    if (keepLoggedIn && loginTime && Date.now() - loginTime > SEVEN_DAYS) {
      signOut(auth).then(() => navigate("/logged-out", { replace: true }));
      return;
    }

    // 30-min inactivity timeout for normal sessions
    if (!keepLoggedIn) {
      const resetTimer = () => {
        clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => {
          signOut(auth).then(() => navigate("/logged-out", { replace: true }));
        }, THIRTY_MIN);
      };

      resetTimer();
      const events = ["mousemove", "keypress", "click", "scroll"];
      events.forEach((e) => window.addEventListener(e, resetTimer));

      return () => {
        clearTimeout(timerRef.current);
        events.forEach((e) => window.removeEventListener(e, resetTimer));
      };
    }
  }, [user, navigate]);

  if (loading || (user && userInfoLoading && !userInfo)) {
    // Match the CRM's loading screen exactly so there's no visual swap when
    // CRMDashboard takes over. Theme comes from localStorage (the user's
    // last setting); defaults to dark which is the app default.
    const theme = (typeof window !== "undefined" && localStorage.getItem("polyp_theme")) || "dark";
    return (
      <div className={`crm-loading ${theme === "light" ? "light-theme" : ""}`}>
        <div className="crm-wave-dots">
          <span className="crm-wave-dot" />
          <span className="crm-wave-dot" />
          <span className="crm-wave-dot" />
          <span className="crm-wave-dot" />
          <span className="crm-wave-dot" />
        </div>
      </div>
    );
  }

  if (!user) return <Navigate to="/login" replace />;

  // Orphan Firebase user: signed in but no backend Users row yet (e.g. an
  // abandoned Google/Apple signup that never collected the phone). Funnel
  // into the signup completion flow — it picks up where they left off. The
  // loading gate above guarantees /me has resolved before this branch fires.
  if (userMissing) return <Navigate to="/signup?from=login" replace />;

  // Strict phone-verification gate. The /me payload (userInfo) is fetched in
  // AuthContext; we require phoneVerified === true. Anything else (false,
  // undefined, or a row that doesn't exist yet) blocks. Existing accounts
  // were backfilled at cutover; brand-new signups stamp this server-side
  // before the Users row is written, so this branch should be unreachable
  // in normal operation — it's the strict fallback.
  if (userInfo && userInfo.phoneVerified !== true) {
    return <PhoneVerifyBlockedScreen />;
  }

  return <Outlet />;
}

function PhoneVerifyBlockedScreen() {
  const navigate = useNavigate();
  const logout = async () => {
    await signOut(auth);
    navigate("/login", { replace: true });
  };

  return (
    <div style={{
      minHeight: "100vh",
      display: "grid",
      placeItems: "center",
      padding: 24,
      background: "var(--bg, #0a0a0a)",
      color: "var(--text-primary, #fff)",
      fontFamily: "-apple-system, BlinkMacSystemFont, system-ui, sans-serif",
    }}>
      <div style={{ maxWidth: 460, textAlign: "center" }}>
        <h1 style={{ fontSize: 28, fontWeight: 600, marginBottom: 12 }}>Verify your phone</h1>
        <p style={{ fontSize: 15, lineHeight: 1.5, color: "var(--text-secondary, #aaa)", marginBottom: 24 }}>
          Your account isn't fully verified. Contact <strong>help@polyp.me</strong> to finish setting up.
        </p>
        <button onClick={logout} style={{ background: "none", border: "none", color: "#888",
          cursor: "pointer", fontSize: 13, textDecoration: "underline" }}>
          Sign out
        </button>
      </div>
    </div>
  );
}
